import {
  Application,
  Container,
  Graphics,
  Sprite,
  Text,
  TextStyle,
  type TilingSprite,
  type Texture,
} from 'pixi.js';
import {
  CENTER_X,
  CENTER_Y,
  DROP_COLLECT_TIME,
  DROP_RING_BAND,
  MAX_DROPS,
  MAX_ENEMIES,
  R_PLANET,
  R_STATION,
  R_SPAWN,
  TAU,
  buildableAt,
  makeRng,
  rand,
  canMerge,
  VIEW_H,
  VIEW_W,
  clamp,
  lerp,
  lerpAngle,
  magnetRadius,
  norm,
  resolveAppearance,
  slotTheta,
  type Content,
  type Slot,
  type World,
} from '@playnets/sim';
import { paletteFor, type SystemPalette } from '@playnets/data';
import { buildJuiceTexture, buildEnemyTextures, buildProjectileTextures } from './enemyArt';
import { drawStructure } from './structureArt';
import { PlanetBodyView } from './planetBody';
import { makeGrainOverlay } from './grain';

/** How long a mount's "just built" flourish lasts. */
const POP_TIME = 0.34;
/** Muzzle flash and recoil. Very short — it repeats several times a second. */
const FIRE_TIME = 0.09;

export interface RendererOptions {
  /** Evolution branch ids, so the body art reflects them (§5.1). */
  evolutions?: readonly string[];
  seed?: number;
}

/**
 * Reads the world and draws it. Owns no game state and never writes back —
 * the sim has no idea this package exists (§12.2).
 */
export class GameRenderer {
  readonly app = new Application();

  private world!: World;
  private content!: Content;
  private palette: SystemPalette = paletteFor('inner-reach');

  /** 720×1280 logical space. One scale transform, applied here and nowhere else. */
  private readonly root = new Container();
  private readonly layerBg = new Container();
  private readonly layerField = new Container();
  private readonly layerGround = new Container();
  private readonly layerPlanet = new Container();
  private readonly layerSlots = new Container();
  private readonly layerEnemies = new Container();
  private readonly layerHealth = new Container();
  private readonly layerShots = new Container();
  private readonly layerDrops = new Container();
  private readonly layerFx = new Container();
  private readonly layerTicks = new Container();

  private bg = new Graphics();
  private starG = new Graphics();
  private dropGlow = new Graphics();
  private stars: Array<{
    x: number;
    y: number;
    r: number;
    a: number;
    depth: number;
    phase: number;
  }> = [];
  private field = new Graphics();
  private ground = new Graphics();
  private fx = new Graphics();
  private ticks = new Graphics();
  private health = new Graphics();

  private planetView!: PlanetBodyView;
  private enemyTex = new Map<string, Texture>();
  private shotTex = new Map<string, Texture>();
  private enemySprites: Sprite[] = [];
  private shotSprites: Sprite[] = [];
  private dropSprites: Sprite[] = [];
  private slotViews: Array<{ container: Container; g: Graphics; label: Text }> = [];

  /** Previous-tick positions, for interpolation between fixed steps. */
  private prevTheta = new Float32Array(MAX_ENEMIES);
  private prevR = new Float32Array(MAX_ENEMIES);
  private prevAlive = new Uint8Array(MAX_ENEMIES);
  private prevRingRot = 0;
  private prevPlanetRot = 0;

  private grain!: TilingSprite;
  private scale = 1;
  /** Vertical offset that keeps the planet centred in a full-bleed canvas. */
  private offsetY = 0;
  private selectedSlot: number | null = null;
  /**
   * Seconds left on each mount's "something just landed here" flourish, and
   * the fingerprint that triggers it. Derived by watching world state rather
   * than by listening for a build event, so it covers building, upgrading and
   * merging without three separate hooks — anything that changes what's on a
   * mount is worth a beat of motion.
   */
  private slotPop: number[] = [];
  private slotKey: string[] = [];
  /**
   * Firing flourish, per mount. A gun's cooldown *rises* on the tick it
   * shoots, so watching for that is enough to know it fired — no new sim
   * event, and it works for every kind that goes through `fireTick`.
   */
  private slotFire: number[] = [];
  private slotCd: number[] = [];
  /** Short-lived kill bursts, seeded from enemies that stopped being alive. */
  private bursts: Array<{ x: number; y: number; t: number; colour: number }> = [];
  /** The structure the player is carrying, and the mount it's hovering over. */
  private placing: string | null = null;
  private hoverSlot: number | null = null;

  static async create(
    world: World,
    content: Content,
    opts: RendererOptions,
  ): Promise<GameRenderer> {
    const r = new GameRenderer();
    await r.init(world, content, opts);
    return r;
  }

  private async init(world: World, content: Content, opts: RendererOptions): Promise<void> {
    this.world = world;
    this.content = content;
    this.palette = paletteFor(world.level.region);

    // The renderer creates and owns its canvas; the host mounts it. Sharing a
    // React-rendered element means a double-invoked effect leaves two
    // Applications on one context and the discarded one removes the live view.
    await this.app.init({
      width: VIEW_W,
      height: VIEW_H,
      backgroundColor: this.palette.void,
      antialias: true,
      autoDensity: true,
      resolution: Math.min(globalThis.devicePixelRatio || 1, 2),
    });

    this.root.addChild(
      this.layerBg,
      this.layerField,
      this.layerGround,
      this.layerPlanet,
      this.layerSlots,
      this.layerEnemies,
      this.layerHealth,
      this.layerShots,
      this.layerDrops,
      this.layerFx,
      this.layerTicks,
    );
    this.app.stage.addChild(this.root);

    this.layerBg.addChild(this.bg, this.starG);
    this.layerDrops.addChild(this.dropGlow);
    this.layerField.addChild(this.field);
    this.layerGround.addChild(this.ground);
    this.layerFx.addChild(this.fx);
    this.layerTicks.addChild(this.ticks);
    this.layerHealth.addChild(this.health);

    const renderer = this.app.renderer;
    this.enemyTex = buildEnemyTextures(renderer, content.enemies.values(), this.palette.paper);
    this.shotTex = buildProjectileTextures(renderer, this.palette.hot);
    const juiceTex = buildJuiceTexture(renderer, this.palette.juice, this.palette.paper);

    for (let i = 0; i < MAX_ENEMIES; i++) {
      const s = new Sprite();
      s.anchor.set(0.5);
      s.visible = false;
      this.layerEnemies.addChild(s);
      this.enemySprites.push(s);
    }
    for (let i = 0; i < world.projectiles.length; i++) {
      const s = new Sprite();
      s.anchor.set(0.5);
      s.visible = false;
      this.layerShots.addChild(s);
      this.shotSprites.push(s);
    }

    for (let i = 0; i < MAX_DROPS; i++) {
      const s = new Sprite(juiceTex);
      s.anchor.set(0.5);
      s.visible = false;
      this.layerDrops.addChild(s);
      this.dropSprites.push(s);
    }

    this.buildPlanet(opts.evolutions ?? [], opts.seed ?? world.seed);
    this.drawStaticField();

    this.grain = makeGrainOverlay(VIEW_W, VIEW_H);
    this.app.stage.addChild(this.grain);

    this.app.ticker.stop(); // the game loop drives us, not Pixi
  }

  private buildPlanet(evolutions: readonly string[], seed: number): void {
    const { body, palette } = resolveAppearance(this.world.planet, evolutions);
    this.planetView = new PlanetBodyView(
      body,
      palette,
      this.world.planet.temperament,
      R_PLANET,
      seed,
    );
    this.planetView.view.position.set(CENTER_X, CENTER_Y);
    this.layerPlanet.addChild(this.planetView.view);
  }

  /** Rebuild the body after an evolution is chosen. New art, same planet. */
  rebuildPlanet(evolutions: readonly string[]): void {
    this.layerPlanet.removeChildren();
    this.planetView.view.destroy({ children: true });
    this.buildPlanet(evolutions, this.world.seed);
  }

  /**
   * Slot views are created on demand rather than once up front, because the
   * Crust Mount and Orbit Mount upgrades add mounts mid-run — a fixed-size pool
   * would leave anything bought during a build phase invisible.
   */
  private slotView(index: number): { container: Container; g: Graphics; label: Text } {
    const existing = this.slotViews[index];
    if (existing) return existing;

    const style = new TextStyle({
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: 11,
      fill: this.palette.paper,
    });
    const container = new Container();
    const g = new Graphics();
    const label = new Text({ text: '', style });
    label.anchor.set(0.5);
    label.position.set(0, 20);
    container.addChild(g, label);
    this.layerSlots.addChild(container);

    const view = { container, g, label };
    this.slotViews[index] = view;
    return view;
  }

  /** The parts that never move: corridors, spawn circle, orbit annulus. */
  private drawStaticField(): void {
    const p = this.palette;
    this.bg.clear();
    // Drawn well beyond the 720×1280 design box: the canvas is full-bleed now,
    // and on a 9:21 phone the extra height shows above and below it.
    this.bg.rect(0, -VIEW_H, VIEW_W, VIEW_H * 3).fill({ color: p.void });

    // Stars. The ground was a flat fill plus grain, which read as paper rather
    // than as space, and the corners of a portrait phone are mostly corners.
    // Two depths: the far field is static, the near field drifts with the
    // orbit, which gives the whole board a sense of being somewhere.
    const rng = makeRng(this.world.seed ^ 0x5eed);
    // The far field is baked straight into the static background: a hundred
    // circles that never move are a hundred circles not worth rebuilding sixty
    // times a second. Only the near field is redrawn, and only because it
    // drifts and twinkles.
    for (let i = 0; i < 100; i++) {
      this.bg
        .circle(
          rand(rng) * VIEW_W,
          -VIEW_H * 0.5 + rand(rng) * VIEW_H * 2,
          0.7 + rand(rng) * 0.9,
        )
        .fill({ color: p.paper, alpha: 0.12 + rand(rng) * 0.16 });
    }
    this.stars.length = 0;
    for (let i = 0; i < 46; i++) {
      this.stars.push({
        x: rand(rng) * VIEW_W,
        y: -VIEW_H * 0.5 + rand(rng) * VIEW_H * 2,
        r: 1.2 + rand(rng) * 1.5,
        a: 0.2 + rand(rng) * 0.3,
        depth: 1,
        phase: rand(rng) * TAU,
      });
    }

    // The two staging corridors. Barely there — they're the telegraph, and the
    // telegraph is the enemies themselves, not a UI band (§6.1). Extra height
    // goes to both of them equally, which is the rule that keeps encounter
    // difficulty identical across devices (§4.0.1).
    const corridor = CENTER_Y - R_SPAWN - 40;
    this.bg.rect(0, -VIEW_H, VIEW_W, corridor + VIEW_H).fill({ color: p.paper, alpha: 0.028 });
    this.bg
      .rect(0, VIEW_H - corridor, VIEW_W, corridor + VIEW_H)
      .fill({ color: p.paper, alpha: 0.028 });
  }

  setSelectedSlot(id: number | null): void {
    this.selectedSlot = id;
  }

  /**
   * What the player is carrying off the build bar, if anything.
   *
   * Carrying a card lights every mount that could take it and dims the rest.
   * That's the answer to the question the bar can't answer on its own — "where
   * does this go?" — and it's why placing can be a drag rather than a menu.
   */
  setPlacing(structureId: string | null, hoverSlot: number | null): void {
    this.placing = structureId;
    this.hoverSlot = hoverSlot;
  }

  /**
   * Capture the current tick's positions before the sim advances. The render
   * host calls this immediately before stepping so `draw(alpha)` has two
   * states to interpolate between.
   */
  snapshot(): void {
    const w = this.world;
    for (let i = 0; i < w.enemies.length && i < MAX_ENEMIES; i++) {
      const e = w.enemies[i]!;
      this.prevTheta[i] = e.theta;
      this.prevR[i] = e.r;
      this.prevAlive[i] = e.alive ? 1 : 0;
    }
    this.prevRingRot = w.ringRot;
    this.prevPlanetRot = w.planetRot;
  }

  /** Called once per animation frame. `alpha` is the fixed-step remainder. */
  draw(alpha: number, dt: number): void {
    const w = this.world;

    const ringRot = lerpAngle(this.prevRingRot, w.ringRot, alpha);
    const planetRot = lerpAngle(this.prevPlanetRot, w.planetRot, alpha);

    this.drawStars(ringRot);
    this.collectBursts();
    this.planetView.update(dt, planetRot, this.nearestThreat(), w.coreHp / Math.max(w.coreMax, 1));
    this.drawField(ringRot);
    this.drawSlots(ringRot, planetRot, dt);
    this.drawEnemies(alpha);
    this.drawEnemyHealth(alpha);
    this.drawShots();
    this.drawDrops();
    this.drawEffects();
    this.drawBursts(dt);
    this.drawStagingTicks(ringRot);
    this.drawIncoming();

    this.app.render();
  }

  /**
   * Anything that was alive last tick and isn't now gets a burst at its last
   * known position. Reading it off `prevAlive` rather than off a kill event
   * keeps the sim unaware that this exists, which is the rule (§12.2), and it
   * catches every cause of death for free.
   */
  private collectBursts(): void {
    const w = this.world;
    for (let i = 0; i < w.enemies.length && i < MAX_ENEMIES; i++) {
      const e = w.enemies[i]!;
      if (this.prevAlive[i] !== 1 || e.alive) continue;
      if (this.bursts.length > 40) break;
      this.bursts.push({
        x: CENTER_X + Math.cos(this.prevTheta[i]!) * this.prevR[i]!,
        y: CENTER_Y + Math.sin(this.prevTheta[i]!) * this.prevR[i]!,
        t: 0,
        colour: this.palette.hot,
      });
    }
  }

  /** Expanding ring plus a handful of shards. Cheap, and it sells the kill. */
  private drawBursts(dt: number): void {
    const g = this.fx;
    const DUR = 0.3;
    for (let i = this.bursts.length - 1; i >= 0; i--) {
      const b = this.bursts[i]!;
      b.t += dt;
      if (b.t >= DUR) {
        this.bursts.splice(i, 1);
        continue;
      }
      const t = b.t / DUR;
      const r = 6 + t * 26;
      g.circle(b.x, b.y, r).stroke({
        width: 3 * (1 - t),
        color: b.colour,
        alpha: 0.85 * (1 - t),
      });
      for (let k = 0; k < 5; k++) {
        const a = (k / 5) * TAU + b.x * 0.01;
        const d = r * 0.85;
        g.circle(b.x + Math.cos(a) * d, b.y + Math.sin(a) * d, 2.4 * (1 - t)).fill({
          color: this.palette.paper,
          alpha: 0.8 * (1 - t),
        });
      }
    }
  }

  /**
   * The near star field only. It slides with the orbit and twinkles, which is
   * what sells the board as a place the camera is hanging over rather than a
   * diagram — the far field is baked into the static background and never
   * touched again.
   */
  private drawStars(ringRot: number): void {
    const g = this.starG;
    const p = this.palette;
    const t = this.world.time;
    g.clear();
    for (const s of this.stars) {
      const x = (((s.x + ringRot * 9) % VIEW_W) + VIEW_W) % VIEW_W;
      const twinkle = 0.75 + Math.sin(t * 1.1 + s.phase) * 0.25;
      g.circle(x, s.y, s.r).fill({ color: p.paper, alpha: s.a * twinkle });
    }
  }

  /** Bearing of whatever is closest to killing us — the planet watches it. */
  private nearestThreat(): number | null {
    let best: number | null = null;
    let bestR = Infinity;
    for (const e of this.world.enemies) {
      if (!e.alive || e.phase === 'enter' || e.phase === 'dead') continue;
      if (e.cloaked && !e.revealed) continue;
      if (e.r < bestR) {
        bestR = e.r;
        best = e.theta;
      }
    }
    return best;
  }

  private drawField(ringRot: number): void {
    const w = this.world;
    const p = this.palette;
    const g = this.field;
    g.clear();

    // Spawn circle: where every attack run begins, on every bearing (§4.0).
    g.circle(CENTER_X, CENTER_Y, R_SPAWN).stroke({
      width: 1,
      color: p.paper,
      alpha: 0.14,
    });

    // The orbit ring. Cyan, so it reads as equipment rather than as a warning.
    g.circle(CENTER_X, CENTER_Y, w.ringR).stroke({ width: 14, color: p.ring, alpha: 0.16 });
    g.circle(CENTER_X, CENTER_Y, w.ringR).stroke({ width: 2, color: p.ring, alpha: 0.7 });

    // Core Integrity: a plain bar under the planet. It was an arc hugging the
    // crust, which read as trim rather than as health — you have to be able to
    // tell at a glance how close you are to losing.
    const frac = clamp(w.coreHp / Math.max(w.coreMax, 1), 0, 1);
    const barW = 104;
    const barX = CENTER_X - barW / 2;
    const barY = CENTER_Y + R_PLANET + 30;
    g.roundRect(barX, barY, barW, 9, 4.5).fill({ color: 0x0a1622, alpha: 0.62 });
    if (frac > 0) {
      g.roundRect(barX + 1.5, barY + 1.5, (barW - 3) * frac, 6, 3).fill({
        // Blue while it's healthy, hot once it isn't. No number, no label.
        color: frac > 0.3 ? 0x4aa3ff : p.threat,
      });
    }

    // The ring's collector band. Faint, but it has to be visible: "the ring
    // picks up juice" is a rule the player can only discover by seeing it
    // happen inside a zone they knew was there.
    g.circle(CENTER_X, CENTER_Y, w.ringR).stroke({
      width: DROP_RING_BAND * 2,
      color: p.juice,
      alpha: 0.06,
    });

    // Magnetics, once bought, shows the crust's own pickup radius.
    const magnet = magnetRadius(w);
    if (magnet > 0) {
      g.circle(CENTER_X, CENTER_Y, R_PLANET + magnet).stroke({
        width: 1.5,
        color: p.juice,
        alpha: 0.35,
      });
    }

    // Index marks riding the orbit. Drawn unconditionally, not per mount: the
    // ring turns on its own now, and on the opening level it carries no mounts
    // at all — without these it would read as a painted circle rather than as
    // something in motion that sweeps juice up as it passes.
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * TAU + ringRot;
      const cos = Math.cos(a);
      const sin = Math.sin(a);
      g.moveTo(CENTER_X + cos * (w.ringR - 7), CENTER_Y + sin * (w.ringR - 7))
        .lineTo(CENTER_X + cos * (w.ringR + 7), CENTER_Y + sin * (w.ringR + 7))
        .stroke({ width: 2, color: p.ring, alpha: 0.5 });
    }
    for (const slot of w.slots) {
      if (slot.layer !== 'ring') continue;
      const a = slot.baseTheta + ringRot;
      g.circle(CENTER_X + Math.cos(a) * w.ringR, CENTER_Y + Math.sin(a) * w.ringR, 3).fill({
        color: p.ring,
        alpha: 0.9,
      });
    }

    // Station platforms: a gantry bracket on a fixed bearing, with a stub
    // strut back toward the planet. They have to read as *built* and as
    // *stationary* — the orbit ticks are sliding past them, and the whole
    // reason to spend on a station is that it never moves.
    for (const slot of w.slots) {
      if (slot.layer !== 'station') continue;
      const a = slot.baseTheta;
      const sr = slot.stationR ?? R_STATION;
      const cos = Math.cos(a);
      const sin = Math.sin(a);
      const cx = CENTER_X + cos * sr;
      const cy = CENTER_Y + sin * sr;

      g.moveTo(CENTER_X + cos * (w.ringR + 12), CENTER_Y + sin * (w.ringR + 12))
        .lineTo(CENTER_X + cos * (sr - 18), CENTER_Y + sin * (sr - 18))
        .stroke({ width: 1.5, color: p.ring, alpha: 0.22 });

      // A short arc of "deck" the mount sits on, tangent to the bearing.
      const half = 22 / sr;
      g.moveTo(CENTER_X + Math.cos(a - half) * sr, CENTER_Y + Math.sin(a - half) * sr)
        .arc(CENTER_X, CENTER_Y, sr, a - half, a + half)
        .stroke({ width: 5, color: p.ring, alpha: 0.35 });
      g.circle(cx, cy, 21).stroke({ width: 1.5, color: p.ring, alpha: 0.3 });
    }

    // Coverage arc for the selected slot only. Showing every arc at once turns
    // the board into a fan chart; showing one answers "what does this cover?".
    if (this.selectedSlot !== null) {
      const slot = w.slots[this.selectedSlot];
      const st = slot?.structure;
      if (slot && st) {
        const def = this.content.structures.get(st.defId);
        const tier = def?.tiers[st.tier];
        if (def && tier && tier.spread > 0 && tier.range > 0) {
          const theta = slotTheta(w, slot);
          const base = this.slotR(slot);
          const half = Math.min(tier.spread, TAU) * 0.5;
          g.moveTo(CENTER_X + Math.cos(theta - half) * base, CENTER_Y + Math.sin(theta - half) * base)
            .arc(CENTER_X, CENTER_Y, base + tier.range, theta - half, theta + half)
            .lineTo(CENTER_X + Math.cos(theta + half) * base, CENTER_Y + Math.sin(theta + half) * base)
            .arc(CENTER_X, CENTER_Y, base, theta + half, theta - half, true)
            .fill({ color: p.hot, alpha: 0.12 });
        }
      }
    }
  }

  private drawSlots(ringRot: number, planetRot: number, dt: number): void {
    const w = this.world;
    for (let i = 0; i < w.slots.length; i++) {
      const slot = w.slots[i]!;
      const view = this.slotView(i);

      // Did what's on this mount just change? Build, upgrade and merge all
      // land as a short overshoot, so spending juice has a physical answer
      // rather than a silent swap.
      const key = slot.structure ? `${slot.structure.defId}:${slot.structure.tier}` : '';
      if (this.slotKey[i] !== key) {
        if (key !== '') this.slotPop[i] = POP_TIME;
        this.slotKey[i] = key;
      }
      const pop = Math.max((this.slotPop[i] ?? 0) - dt, 0);
      this.slotPop[i] = pop;

      // Did it just shoot? A gun's cooldown only ever rises on the tick it
      // fires, so the jump is the tell.
      const cd = slot.structure?.cooldown ?? 0;
      if (cd > (this.slotCd[i] ?? 0) + 1e-4) this.slotFire[i] = FIRE_TIME;
      this.slotCd[i] = cd;
      const fire = Math.max((this.slotFire[i] ?? 0) - dt, 0);
      this.slotFire[i] = fire;

      const theta = this.slotAngle(slot, ringRot, planetRot);
      const r = this.slotR(slot);
      view.container.position.set(
        CENTER_X + Math.cos(theta) * r,
        CENTER_Y + Math.sin(theta) * r,
      );
      // Structures point outward, away from the crust.
      view.container.rotation = theta + Math.PI / 2;
      // A squash-free overshoot: out past full size, then settle. Half a beat,
      // because it fires mid-wave too and must never read as a hitch.
      const t = pop / POP_TIME;
      view.container.scale.set(1 + Math.sin(t * Math.PI) * 0.42 * (1 - t * 0.35));

      // Recoil rides the same transform: the hardware sinks toward the crust
      // for a fraction of a second, which is what makes a row of guns look
      // like it's working rather than emitting sprites.
      const kick = (fire / FIRE_TIME) * 4;
      view.container.position.set(
        view.container.x + Math.cos(theta) * -kick,
        view.container.y + Math.sin(theta) * -kick,
      );

      const g = view.g;
      g.clear();
      const st = slot.structure;
      if (!st) {
        // An empty mount is an invitation, not a warning. It used to be drawn
        // in the hot accent, which on an orange-heavy palette read as an error
        // state; it's the cool orbital cyan now — equipment, not alarm — and a
        // hexagonal port rather than a dashed circle.
        // A mount with nothing to offer this level is drawn dormant: no glow,
        // no plus, no breathing. Advertising a socket you can't use is worse
        // than not drawing it.
        const live = buildableAt(w, this.content, slot).length > 0;
        if (!live) {
          g.circle(0, 0, 11).stroke({
            width: 1.5,
            color: this.palette.paper,
            alpha: 0.16,
          });
          continue;
        }

        // While a card is being carried, every mount answers "can I go here?"
        // before it's dropped: valid ones brighten and grow, the rest recede.
        // A mount that can't take the carried gun still has to be visible —
        // vanishing sockets would read as the board changing shape.
        const carrying = this.placing !== null;
        const takes = carrying ? this.acceptsPlacing(slot) : true;
        const hovered = this.hoverSlot === slot.id && takes;

        const pulse = 1 + Math.sin(this.world.time * 2.2 + slot.id) * 0.06;
        const r = (hovered ? 21 : 15) * pulse;
        const c = this.palette.ring;
        const dim = carrying && !takes ? 0.3 : 1;

        g.circle(0, 0, r * 1.25).fill({ color: c, alpha: 0.1 * dim });
        if (carrying && takes) {
          g.circle(0, 0, r * (hovered ? 1.9 : 1.55)).fill({ color: c, alpha: 0.14 });
        }
        const hex: number[] = [];
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * TAU - Math.PI / 2;
          hex.push(Math.cos(a) * r, Math.sin(a) * r);
        }
        g.poly(hex).fill({ color: c, alpha: 0.16 * dim });
        g.poly(hex).stroke({ width: 2.2, color: c, alpha: 0.8 * dim });

        g.moveTo(-r * 0.38, 0).lineTo(r * 0.38, 0);
        g.moveTo(0, -r * 0.38).lineTo(0, r * 0.38);
        g.stroke({ width: 2.4, color: c, alpha: 0.9 * dim, cap: 'round' });

        if (hovered) {
          g.circle(0, 0, r + 8).stroke({ width: 3, color: this.palette.paper, alpha: 0.95 });
        } else if (this.selectedSlot === slot.id) {
          g.circle(0, 0, r + 7).stroke({ width: 2.5, color: this.palette.paper, alpha: 0.9 });
        }
        continue;
      }
      const def = this.content.structures.get(st.defId);
      if (!def) continue;
      drawStructure(g, def, st.tier, st.spin);

      // Muzzle flash, in local space so it points where the barrels do.
      if (fire > 0 && def.kind !== 'moon' && def.kind !== 'mine' && def.kind !== 'support') {
        const f = fire / FIRE_TIME;
        const tipY = -34 * (1 + (st.tier / Math.max(def.tiers.length - 1, 1)) * 0.45);
        g.circle(0, tipY, 11 * f).fill({ color: this.palette.paper, alpha: 0.55 * f });
        g.circle(0, tipY, 6 * f).fill({ color: def.colour, alpha: 0.9 * f });
      }
      if (slot.locked) {
        g.circle(0, 0, 13).stroke({ width: 1.5, color: this.palette.paper, alpha: 0.5 });
      }

      // A maxed gun that the carried card would transform. Hot rather than
      // cyan, and pulsing: this isn't "put it here", it's "spend it into
      // this", and the two shouldn't look like the same offer.
      if (this.acceptsMerge(slot)) {
        // Wide enough to enclose a maxed gun rather than be clipped by one —
        // a maxed turret is ~44 units across at the top of its ladder.
        const beat = 1 + Math.sin(this.world.time * 5) * 0.06;
        const hot = this.palette.hot;
        g.circle(0, 0, 42 * beat).fill({ color: hot, alpha: 0.14 });
        g.circle(0, 0, 42 * beat).stroke({ width: 3, color: hot, alpha: 0.95 });
        if (this.hoverSlot === slot.id) {
          g.circle(0, 0, 50 * beat).stroke({ width: 3, color: this.palette.paper, alpha: 0.95 });
        }
      } else if (this.placing !== null) {
        // Carrying something this mount can't use: recede, same as an empty
        // one of the wrong layer.
        g.circle(0, 0, 22).fill({ color: this.palette.void, alpha: 0.45 });
      }

      if (this.selectedSlot === slot.id) {
        g.circle(0, 0, 16).stroke({ width: 2, color: this.palette.hot, alpha: 0.9 });
      }
    }
  }

  /** Would the carried card go on this empty mount? Layer and level rules only. */
  private acceptsPlacing(slot: Slot): boolean {
    if (this.placing === null) return false;
    return buildableAt(this.world, this.content, slot).some((d) => d.id === this.placing);
  }

  /** Would it merge into what's already here? Price excluded, as everywhere. */
  private acceptsMerge(slot: Slot): boolean {
    if (this.placing === null || !slot.structure) return false;
    const why = canMerge(this.world, this.content, slot, this.placing);
    return why.ok || why.reason === 'juice';
  }

  private slotAngle(slot: Slot, ringRot: number, planetRot: number): number {
    // Interpolated rotations rather than the sim's, so mounts don't judder;
    // a station has no rotation to interpolate.
    if (slot.layer === 'station') return slot.baseTheta;
    if (slot.layer === 'ring') return slot.baseTheta + ringRot;
    if (slot.locked) return slot.lockTheta;
    return slot.baseTheta + planetRot;
  }

  private slotR(slot: Slot): number {
    if (slot.layer === 'station') return slot.stationR ?? R_STATION;
    return slot.layer === 'ring' ? this.world.ringR : R_PLANET;
  }

  private drawEnemies(alpha: number): void {
    const w = this.world;
    for (let i = 0; i < this.enemySprites.length; i++) {
      const sprite = this.enemySprites[i]!;
      const e = w.enemies[i];
      if (!e || !e.alive) {
        sprite.visible = false;
        continue;
      }
      // A cloaked wraith with no radar coverage is genuinely not drawn.
      if (e.cloaked && !e.revealed) {
        sprite.visible = false;
        continue;
      }

      const tex = this.enemyTex.get(e.def.id);
      if (!tex) {
        sprite.visible = false;
        continue;
      }
      if (sprite.texture !== tex) sprite.texture = tex;

      // Only interpolate for entities that were alive last tick; a fresh spawn
      // would otherwise streak in from wherever the pool slot was last used.
      const useLerp = this.prevAlive[i] === 1;
      const theta = useLerp ? lerpAngle(this.prevTheta[i]!, e.theta, alpha) : e.theta;
      const r = useLerp ? lerp(this.prevR[i]!, e.r, alpha) : e.r;

      sprite.visible = true;
      sprite.position.set(CENTER_X + Math.cos(theta) * r, CENTER_Y + Math.sin(theta) * r);
      // Nose toward the planet.
      sprite.rotation = theta - Math.PI / 2;

      // Holding on the spawn ring: pulse in the family colour. Silhouette plus
      // colour is the whole read — no icons, no labels (§6.1).
      if (e.phase === 'hold') {
        const t = (e.phaseT * 6) % TAU;
        sprite.alpha = 0.7 + Math.sin(t) * 0.3;
        sprite.scale.set(1 + Math.sin(t) * 0.06);
      } else if (e.freezeT > 0) {
        sprite.alpha = 0.85;
        sprite.scale.set(1);
        sprite.tint = 0x9fd8ea;
      } else {
        sprite.alpha = e.underRing ? 0.55 : 1;
        sprite.scale.set(1);
        sprite.tint = e.burnT > 0 ? 0xffb08a : 0xffffff;
      }
      if (e.freezeT <= 0 && e.burnT <= 0 && e.phase !== 'hold') sprite.tint = 0xffffff;
    }
  }

  /**
   * A small bar over anything that's been hit and is still alive. Hidden at
   * full health on purpose: a bar over every drone in a fan of twelve is
   * clutter, and the interesting information is "that one is nearly dead".
   */
  private drawEnemyHealth(alpha: number): void {
    const w = this.world;
    const g = this.health;
    g.clear();

    for (let i = 0; i < w.enemies.length; i++) {
      const e = w.enemies[i]!;
      if (!e.alive || e.phase === 'enter' || e.phase === 'dead') continue;
      if (e.cloaked && !e.revealed) continue;
      const frac = e.hp / Math.max(e.maxHp, 1);
      if (frac >= 0.999) continue;

      const useLerp = this.prevAlive[i] === 1;
      const theta = useLerp ? lerpAngle(this.prevTheta[i]!, e.theta, alpha) : e.theta;
      const r = useLerp ? lerp(this.prevR[i]!, e.r, alpha) : e.r;
      const x = CENTER_X + Math.cos(theta) * r;
      const y = CENTER_Y + Math.sin(theta) * r - e.def.size - 8;

      // Sized to the thing it belongs to, so a boss bar isn't a drone bar.
      const bw = Math.max(18, e.def.size * 1.6);
      g.roundRect(x - bw / 2, y, bw, 5, 2.5).fill({ color: 0x0a1622, alpha: 0.7 });
      g.roundRect(x - bw / 2 + 1, y + 1, (bw - 2) * clamp(frac, 0, 1), 3, 1.5).fill({
        color: frac > 0.5 ? 0x7ee08a : frac > 0.25 ? 0xffd166 : this.palette.threat,
      });
    }
  }

  private drawShots(): void {
    const w = this.world;
    for (let i = 0; i < this.shotSprites.length; i++) {
      const sprite = this.shotSprites[i]!;
      const p = w.projectiles[i];
      if (!p || !p.alive) {
        sprite.visible = false;
        continue;
      }
      const tex = this.shotTex.get(p.kind) ?? this.shotTex.get('bolt');
      if (!tex) continue;
      if (sprite.texture !== tex) sprite.texture = tex;
      sprite.visible = true;
      sprite.position.set(CENTER_X + Math.cos(p.theta) * p.r, CENTER_Y + Math.sin(p.theta) * p.r);
      sprite.rotation = p.theta - Math.PI / 2;
    }
  }

  /**
   * Juice. Three things have to be legible at a glance and at speed: that it's
   * money, that it's about to expire, and that it's been taken. Money is the
   * rim, expiry is a blink over the last third of its life, and collection is
   * a short flight toward the counter in the top-left corner.
   */
  private drawDrops(): void {
    const w = this.world;
    const glow = this.dropGlow;
    glow.clear();
    for (let i = 0; i < this.dropSprites.length; i++) {
      const sprite = this.dropSprites[i]!;
      const d = w.drops[i];
      if (!d || !d.alive) {
        sprite.visible = false;
        continue;
      }

      sprite.visible = true;
      const x = CENTER_X + Math.cos(d.theta) * d.r;
      const y = CENTER_Y + Math.sin(d.theta) * d.r;

      if (d.collected) {
        // Flies to the juice counter, so where your money went is never a
        // mystery. Purely cosmetic; the sim credited it on pickup.
        const t = clamp(d.collectT / DROP_COLLECT_TIME, 0, 1);
        const ease = t * t;
        sprite.position.set(lerp(x, 64, ease), lerp(y, 74, ease));
        sprite.scale.set(1 - t * 0.55);
        sprite.alpha = 1 - ease;
        sprite.rotation = t * 3;
        continue;
      }

      sprite.position.set(x, y);
      sprite.rotation = 0;

      const life = d.maxLife > 0 ? d.life / d.maxLife : 1;
      // A gentle bob so juice read as objects sitting there, not as UI pips.
      const bob = 1 + Math.sin(w.time * 5 + d.id) * 0.06;
      sprite.scale.set(bob);
      // A halo under it. Juice is the one thing on the board you're meant to
      // want, and a flat chip on a dark ground doesn't ask to be picked up.
      glow.circle(x, y, 13 * bob).fill({
        color: this.palette.juice,
        alpha: 0.16 * (life > 0.34 ? 1 : 0.5),
      });
      // Last third of its life: blink, faster as it runs out.
      sprite.alpha =
        life > 0.34 ? 1 : 0.35 + 0.65 * Math.abs(Math.sin(w.time * (10 + (1 - life) * 22)));
    }
  }

  private drawEffects(): void {
    const w = this.world;
    const p = this.palette;
    const g = this.fx;
    const ground = this.ground;
    g.clear();
    ground.clear();

    for (const e of w.effects) {
      if (!e.alive) continue;
      const life = e.duration > 0 ? Math.max(0, e.t) / e.duration : 1;

      switch (e.kind) {
        case 'pulse': {
          if (e.t < 0) break;
          g.circle(CENTER_X, CENTER_Y, e.front).stroke({
            width: 10 * (1 - life) + 3,
            color: p.hot,
            alpha: 0.8 * (1 - life) + 0.2,
          });
          break;
        }
        case 'cone': {
          const half = e.spread * 0.5;
          g.moveTo(CENTER_X, CENTER_Y)
            .arc(CENTER_X, CENTER_Y, R_SPAWN + 40, e.theta - half, e.theta + half)
            .lineTo(CENTER_X, CENTER_Y)
            .fill({ color: p.hot, alpha: 0.55 * (1 - life) });
          break;
        }
        case 'freeze': {
          g.circle(CENTER_X, CENTER_Y, e.front).fill({
            color: 0x7fd4e8,
            alpha: 0.16 * (1 - life * 0.6),
          });
          break;
        }
        case 'ground': {
          // Sits under the enemies — it's terrain, not a flash.
          const half = e.spread * 0.5;
          if (e.spread >= TAU) {
            ground.circle(CENTER_X, CENTER_Y, e.front).fill({
              color: p.hot,
              alpha: 0.14 * (1 - life * 0.7),
            });
          } else {
            ground
              .moveTo(CENTER_X, CENTER_Y)
              .arc(CENTER_X, CENTER_Y, e.front, e.theta - half, e.theta + half)
              .lineTo(CENTER_X, CENTER_Y)
              .fill({ color: p.hot, alpha: 0.2 * (1 - life * 0.7) });
          }
          break;
        }
        case 'singularity': {
          const x = CENTER_X + Math.cos(e.theta) * e.front;
          const y = CENTER_Y + Math.sin(e.theta) * e.front;
          g.circle(x, y, 26 + Math.sin(e.t * 9) * 5).stroke({
            width: 3,
            color: p.hot,
            alpha: 0.85,
          });
          for (let i = 0; i < 10; i++) {
            const a = e.theta + (i / 10 - 0.5) * 2.4;
            g.moveTo(CENTER_X + Math.cos(a) * R_SPAWN, CENTER_Y + Math.sin(a) * R_SPAWN)
              .lineTo(x, y)
              .stroke({ width: 1, color: p.hot, alpha: 0.18 });
          }
          break;
        }
        case 'ringstorm': {
          g.circle(CENTER_X, CENTER_Y, w.ringR).stroke({
            width: 6 + Math.sin(e.t * 18) * 3,
            color: p.hot,
            alpha: 0.7,
          });
          break;
        }
        case 'impact': {
          const x1 = CENTER_X + Math.cos(e.theta) * e.r;
          const y1 = CENTER_Y + Math.sin(e.theta) * e.r;
          const x2 = CENTER_X + Math.cos(e.theta + e.spread) * e.front;
          const y2 = CENTER_Y + Math.sin(e.theta + e.spread) * e.front;
          g.moveTo(x1, y1).lineTo(x2, y2).stroke({
            width: 2,
            color: p.hot,
            alpha: 0.9 * (1 - life),
          });
          break;
        }
        case 'shatter': {
          const x = CENTER_X + Math.cos(e.theta) * e.r;
          const y = CENTER_Y + Math.sin(e.theta) * e.r;
          for (let i = 0; i < 6; i++) {
            const a = e.theta + (i / 6) * TAU;
            const d = e.front + life * 22;
            g.moveTo(x, y)
              .lineTo(x + Math.cos(a) * d, y + Math.sin(a) * d)
              .stroke({ width: 2, color: 0xd6f0fa, alpha: 1 - life });
          }
          break;
        }
        case 'core-hit': {
          const half = e.spread * 0.5;
          g.moveTo(CENTER_X, CENTER_Y)
            .arc(CENTER_X, CENTER_Y, R_PLANET + 14, e.theta - half, e.theta + half)
            .lineTo(CENTER_X, CENTER_Y)
            .fill({ color: p.threat, alpha: 0.5 * (1 - life) });
          break;
        }
      }
    }
  }

  /**
   * A faint tick on the orbit ring at each staged bearing. This is the only
   * diegetic-breaking element in the playfield and it should be almost
   * invisible (§6.1) — it exists so you can judge a spin without looking away
   * from your own defences.
   */
  private drawStagingTicks(ringRot: number): void {
    const w = this.world;
    const g = this.ticks;
    g.clear();
    void ringRot;

    for (const e of w.enemies) {
      if (!e.alive) continue;
      if (e.phase !== 'stage' && e.phase !== 'hold') continue;
      if (e.cloaked && !e.revealed) continue;
      const a = e.bearing;
      const r0 = w.ringR + 10;
      const r1 = w.ringR + 17;
      g.moveTo(CENTER_X + Math.cos(a) * r0, CENTER_Y + Math.sin(a) * r0)
        .lineTo(CENTER_X + Math.cos(a) * r1, CENTER_Y + Math.sin(a) * r1)
        .stroke({ width: 2, color: e.def.colour, alpha: 0.45 });
    }
  }

  /**
   * Where the next wave is coming from, drawn on the bearings it will actually
   * use — the wave is queued when the build phase opens, so even the groups
   * that pick a random bearing are already decided and the preview can't lie.
   *
   * A row of chips at the bottom told you *what* was coming but not *where*,
   * which is the half that decides where you build.
   */
  private drawIncoming(): void {
    const w = this.world;
    const g = this.ticks;
    if (w.waveState !== 'prep' || w.pending.length === 0) return;

    // Bucket by bearing so a fan of twelve is one marker, not twelve.
    const buckets = new Map<number, { count: number; colour: number; theta: number }>();
    for (const p of w.pending) {
      const key = Math.round(norm(p.bearing) / (TAU / 24));
      const found = buckets.get(key);
      if (found) found.count++;
      else buckets.set(key, { count: 1, colour: p.enemy.colour, theta: norm(p.bearing) });
    }

    const pulse = 0.75 + Math.sin(w.time * 3) * 0.25;
    for (const b of buckets.values()) {
      const r = R_SPAWN + 30;
      const x = CENTER_X + Math.cos(b.theta) * r;
      const y = CENTER_Y + Math.sin(b.theta) * r;

      // An arrowhead pointing inward: this is the way in.
      const back = 13;
      const wide = 9;
      const nose = b.theta + Math.PI;
      g.poly([
        x + Math.cos(nose) * back,
        y + Math.sin(nose) * back,
        x + Math.cos(nose + 2.3) * wide,
        y + Math.sin(nose + 2.3) * wide,
        x + Math.cos(nose - 2.3) * wide,
        y + Math.sin(nose - 2.3) * wide,
      ]).fill({ color: b.colour, alpha: pulse });

      // And a stub of the lane it will fly down.
      g.moveTo(CENTER_X + Math.cos(b.theta) * (r + 8), CENTER_Y + Math.sin(b.theta) * (r + 8));
      g.lineTo(CENTER_X + Math.cos(b.theta) * (r + 30), CENTER_Y + Math.sin(b.theta) * (r + 30));
      g.stroke({ width: 3, color: b.colour, alpha: 0.35 * pulse });
    }
  }

  /** Core-hit feedback goes through the planet body, not a screen flash. */
  flinch(strength = 1): void {
    this.planetView.flinch(strength);
  }

  /**
   * Fill the host edge to edge. Scale comes from width, always (§4.0.1) — never
   * from height, or encounter tuning drifts per device — and the planet stays
   * centred, so a taller screen gives both corridors more telegraph rather than
   * giving the game a letterbox border.
   *
   * The one guard: if the host is so short that scaling by width would push the
   * spawn ring off-screen, fall back to whatever keeps the playfield visible.
   * The playfield is never allowed to be cropped.
   */
  resize(cssWidth: number, cssHeight: number): void {
    const PLAYFIELD = (R_SPAWN + 46) * 2;
    const scale = Math.min(cssWidth / VIEW_W, cssHeight / PLAYFIELD);
    this.scale = scale;
    this.offsetY = cssHeight / 2 - CENTER_Y * scale;

    this.app.renderer.resize(cssWidth, cssHeight);
    this.root.scale.set(scale);
    this.root.position.set(cssWidth / 2 - CENTER_X * scale, this.offsetY);

    this.grain.width = cssWidth;
    this.grain.height = cssHeight;
  }

  /** Client pixel → logical 720×1280 unit. Input lives in sim units. */
  toLogical(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.app.canvas.getBoundingClientRect();
    return {
      x: (clientX - rect.left - this.root.position.x) / this.scale,
      y: (clientY - rect.top - this.offsetY) / this.scale,
    };
  }

  /** The canvas this renderer draws into. Mount it behind the input overlay. */
  get canvas(): HTMLCanvasElement {
    return this.app.canvas;
  }

  destroy(): void {
    this.app.destroy({ removeView: true }, { children: true, texture: false });
  }
}
